import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Close as CloseIcon, Event } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

export type RosterStatusFilter = 'upcoming' | 'noUpcoming' | 'never';

export const ROSTER_STATUS_FILTER_OPTIONS: { value: RosterStatusFilter; label: string; description: string }[] = [
  {
    value: 'upcoming',
    label: 'Has Upcoming Roster',
    description: 'Rostered onto at least one published roster from today onwards.',
  },
  {
    value: 'noUpcoming',
    label: 'No Upcoming Roster',
    description: 'Not rostered onto anything from today onwards, including volunteers never rostered.',
  },
  {
    value: 'never',
    label: 'Never Rostered',
    description: 'Never rostered onto a published roster.',
  },
];

const useStyles = makeStyles(() =>
  createStyles({
    dialogTitle: {
      paddingBottom: '8px',
    },
    description: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '16px',
    },
    option: {
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    optionLabel: {
      fontSize: '14px',
      fontWeight: 600,
    },
    optionDescription: {
      fontSize: '13px',
      color: '#6b7280',
    },
    dialogActions: {
      padding: '16px 24px',
      borderTop: '1px solid #e5e7eb',
    },
  })
);

interface RosterSearchDialogProps {
  open: boolean;
  value: RosterStatusFilter | null;
  onClose: () => void;
  onSearch: (value: RosterStatusFilter) => void;
}

export const RosterSearchDialog = ({ open, value, onClose, onSearch }: RosterSearchDialogProps) => {
  const { isMobile } = useCampfireTheme();
  const classes = useStyles();
  const [selected, setSelected] = useState<RosterStatusFilter | null>(value);

  useEffect(() => {
    if (open) {
      setSelected(value);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth='sm' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Roster Search</Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography className={classes.description}>
          Find volunteers by their rostering. Cancelled dates and removed rosterings are not counted.
        </Typography>
        <RadioGroup value={selected ?? ''} onChange={(event) => setSelected(event.target.value as RosterStatusFilter)}>
          {ROSTER_STATUS_FILTER_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              className={classes.option}
              control={<Radio color='primary' size='small' />}
              label={
                <Box paddingTop='6px'>
                  <Typography className={classes.optionLabel}>{option.label}</Typography>
                  <Typography className={classes.optionDescription}>{option.description}</Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <TabletButton variant='text' color='error' onClick={onClose} style={{ marginRight: 8, textTransform: 'none' }}>
          Cancel
        </TabletButton>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={() => selected && onSearch(selected)}
          disabled={!selected}
          startIcon={<Event />}
          style={{ textTransform: 'none' }}
        >
          Search
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
